// hooks/useCurrentUser.ts
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { logAndThrow } from '../utils/logging';
import { User, mapDbRoleToEnum, RoleAssignment, UserRole } from '../types';

export function useCurrentUser(userId?: string) {
    return useQuery<User | null>({
        queryKey: ['currentUser', userId],
        queryFn: async () => {
            if (!userId) return null;

            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('id, full_name, avatar_url, team_id')
                .eq('id', userId)
                .single();

            if (profileError) return logAndThrow('fetch current user profile', profileError, { userId });

            const { data: roleRows, error: roleError } = await supabase
                .from('user_roles')
                .select('roles(name)')
                .eq('user_id', userId);

            if (roleError) return logAndThrow('fetch user_roles', roleError, { userId });

            const roleNames = (roleRows || [])
                .map((row: any) => row.roles?.name as string | undefined)
                .filter((name): name is string => !!name);

            console.log('[useCurrentUser] User roles fetched:', { userId, roleNames });

            let scopedAssignments: RoleAssignment[] = [];
            try {
                const { data: scopedRows, error: scopedError } = await supabase
                    .from('user_league_roles')
                    .select('role_name, league_id')
                    .eq('user_id', userId);

                if (scopedError) throw scopedError;

                console.log('[useCurrentUser] League roles fetched:', { userId, scopedRows });

                const grouped = new Map<string, Set<string>>();
                (scopedRows || []).forEach((row: any) => {
                    const key = row.role_name as string;
                    if (!grouped.has(key)) {
                        grouped.set(key, new Set<string>());
                    }
                    if (row.league_id) {
                        grouped.get(key)!.add(row.league_id);
                    }
                });

                scopedAssignments = Array.from(grouped.entries()).map(([roleName, leagueSet], index) => ({
                    role: mapDbRoleToEnum(roleName),
                    leagueIds: Array.from(leagueSet),
                    isPrimary: index === 0,
                }));
            } catch (err) {
                // Table might not exist yet; fallback to global assignments.
                scopedAssignments = [];
            }

            if (scopedAssignments.length === 0) {
                scopedAssignments = roleNames.map((roleName, index) => ({
                    role: mapDbRoleToEnum(roleName),
                    leagueIds: [],
                    isPrimary: index === 0,
                }));
            }

            const primaryRole = scopedAssignments.find((assignment) => assignment.isPrimary)?.role || scopedAssignments[0]?.role || UserRole.VIEWER;
            const leagueIds = Array.from(new Set(scopedAssignments.flatMap((assignment) => assignment.leagueIds || [])));

            console.log('[useCurrentUser] User data built:', {
                userId,
                primaryRole,
                leagueIds,
                scopedAssignments: scopedAssignments.map(a => ({ role: a.role, leagueIds: a.leagueIds, isPrimary: a.isPrimary }))
            });

            return {
                id: profileData.id,
                full_name: profileData.full_name || 'Unknown User',
                avatar_url: profileData.avatar_url,
                role: primaryRole,
                primaryRole,
                roles: scopedAssignments,
                leagueIds,
                teamId: profileData.team_id,
            };
        },
        enabled: !!userId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}